import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react'

interface LightboxImage {
  src: string
  alt: string
}

interface LightboxContextType {
  images: LightboxImage[]
  registerImage: (img: LightboxImage) => void
  openLightbox: (src: string) => void
}

const LightboxContext = createContext<LightboxContextType>({
  images: [],
  registerImage: () => {},
  openLightbox: () => {},
})

export const useLightbox = () => useContext(LightboxContext)

export const LightboxProvider = ({ children }: { children: React.ReactNode }) => {
  const [images, setImages] = useState<LightboxImage[]>([])
  const [currentIndex, setCurrentIndex] = useState(-1)
  const isOpen = currentIndex >= 0
  const touchStartX = useRef<number | null>(null)

  const registerImage = useCallback((img: LightboxImage) => {
    setImages(prev => {
      if (prev.some(i => i.src === img.src)) return prev
      return [...prev, img]
    })
  }, [])

  const openLightbox = useCallback((src: string) => {
    const idx = images.findIndex(i => i.src === src)
    if (idx >= 0) setCurrentIndex(idx)
  }, [images])

  const close = useCallback(() => setCurrentIndex(-1), [])
  const prev = useCallback(() => setCurrentIndex(i => i > 0 ? i - 1 : images.length - 1), [images.length])
  const next = useCallback(() => setCurrentIndex(i => i < images.length - 1 ? i + 1 : 0), [images.length])

  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      if (e.key === 'ArrowLeft') prev()
      if (e.key === 'ArrowRight') next()
    }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handleKey)
    }
  }, [isOpen, close, prev, next])

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return
    const deltaX = e.changedTouches[0].clientX - touchStartX.current
    if (Math.abs(deltaX) > 50) {
      deltaX < 0 ? next() : prev()
    }
    touchStartX.current = null
  }

  return (
    <LightboxContext.Provider value={{ images, registerImage, openLightbox }}>
      {children}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          onClick={close}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="absolute inset-0 bg-black/95"></div>

          <button
            onClick={(e) => { e.stopPropagation(); close() }}
            className="absolute top-4 right-4 z-10 w-12 h-12 flex items-center justify-center text-white/70 hover:text-white text-3xl transition-colors"
            aria-label="Close"
          >
            &times;
          </button>

          <div className="relative z-10 max-w-[90vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={images[currentIndex]?.src}
              alt={images[currentIndex]?.alt}
              className="max-w-full max-h-[85vh] object-contain rounded"
            />
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); prev() }}
            className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-black/40 rounded-full"
            aria-label="Previous image"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#facc15" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); next() }}
            className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-black/40 rounded-full"
            aria-label="Next image"
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#facc15" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 text-white/50 text-sm font-poppins">
            {currentIndex + 1} / {images.length}
          </div>
        </div>
      )}
    </LightboxContext.Provider>
  )
}

export const LightboxImage = ({ src, alt, className, wrapperClassName }: {
  src: string
  alt: string
  className?: string
  wrapperClassName?: string
}) => {
  const { registerImage, openLightbox } = useLightbox()

  useEffect(() => {
    registerImage({ src, alt })
  }, [src, alt, registerImage])

  return (
    <div
      className={`cursor-pointer overflow-hidden ${wrapperClassName || ''}`}
      onClick={() => openLightbox(src)}
    >
      <img src={src} alt={alt} className={`transition-transform duration-500 hover:scale-110 ${className || ''}`} />
    </div>
  )
}
